import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import { BiError } from "react-icons/bi";
import { setCookie } from "../config/webStorage.js";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const UserLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();

  const loginHandler = async (e) => {
    e.preventDefault();

    if (!email) return setError("Please enter email address");
    if (!password) return setError("Please enter password");

    try {
      setLoginLoading(true);
      setError("");

      const { data } = await axios.post(`${apiurl}/api/user/login`, {
        email: email.toLowerCase(),
        password,
      });

      const { success, message, accessToken, refreshToken, user } = data;

      if (!success) {
        setError(message || "Login failed. Please try again.");
        return;
      }

      // 2FA check
      if (user?.twoFactorAuth?.enabled) {
        navigate("/verify-otp", {
          state: {
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            rememberMe,
          },
        });
      } else {
        const token = rememberMe ? refreshToken : accessToken;
        setCookie("SWFMSToken", token, rememberMe ? 2505600 : 25200);
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex items-center justify-center px-4">
      <div className="max-w-md w-full flex flex-col md:flex-row shadow-lg border border-[#E0DED9] rounded-2xl overflow-hidden">
        <div className="w-full p-8 md:p-10 bg-white">
          <div className="flex justify-center mb-6">
            <img src="/suwasthi-logo-brown.png" className="h-20" alt="Suwasthi Logo" />
          </div>

          <h2 className="text-2xl font-bold text-[#2F2E41] text-center">Welcome Back</h2>
          <p className="text-sm text-gray-500 text-center mb-8">
            Login to manage your stock and inventory
          </p>

          <form onSubmit={loginHandler} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm text-[#444] font-medium">Email</label>
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 bg-[#EDEDED] border border-[#ccc] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-[#444] font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-[#EDEDED] border border-[#ccc] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="text-[#E63946] text-sm flex items-center gap-2">
                <BiError /> {error}
              </div>
            )}

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  className="form-checkbox rounded border-gray-300"
                />
                Remember Me
              </label>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-[#2F2E41] text-white font-medium rounded-md hover:bg-[#3c3c50] transition"
            >
              {loginLoading ? "Loading..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;